import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { PrintService } from '../../services/print.service';
import { TaxesService } from '../../services/taxes.service';

export interface TaxAuthorityReportLineAr {
	trandate: string;
	type: number;
	typename: string;
	transno: string;
	debtorno: string;
	name: string;
	branchcode: string;
	netamount: number;
	tax: number;
}

export interface TaxAuthorityReportLineAp {
	trandate: string;
	type: number;
	typename: string;
	transno: string;
	suppname: string;
	suppreference: string;
	netamount: number;
	taxamt: number;
}

export interface TaxAuthorityReportBlock {
	taxid: number;
	description: string;
	sales: { count: number; net: number; tax: number; total: number; lines: TaxAuthorityReportLineAr[] };
	purchases: { count: number; net: number; tax: number; total: number; lines: TaxAuthorityReportLineAp[] };
	difference: { count: number; net: number; tax: number; total: number };
}

export interface TaxAuthorityReportPayload {
	success: boolean;
	error?: string;
	company_name?: string;
	title?: string;
	subtitle?: string;
	period_end_display?: string;
	no_of_periods?: number;
	ar_from_period?: number;
	ar_to_period?: number;
	purchase_date_from?: string;
	purchase_date_to?: string;
	detail?: boolean;
	footnotes?: string[];
	authorities?: TaxAuthorityReportBlock[];
	grand?: {
		sales: { count: number; net: number; tax: number; total: number };
		purchases: { count: number; net: number; tax: number; total: number };
		difference: { count: number; net: number; tax: number; total: number };
	};
	has_data?: boolean;
}

@Component({
	selector: 'app-tax-authority-report',
	templateUrl: './tax-authority-report.component.html',
	styleUrls: ['./tax-authority-report.component.scss']
})
export class TaxAuthorityReportComponent implements OnInit {
	@ViewChild('print_table') printTableRef: ElementRef;

	formGroup: UntypedFormGroup;
	loading = false;
	exporting = false;
	report: TaxAuthorityReportPayload | null = null;
	color: string;

	periods: Array<{ periodno: number; lastdate_in_period: string }> = [];
	allAuthorities: Array<{ taxid: number; description: string }> = [];
	toAuthorityOptions: Array<{ taxid: number; description: string }> = [];

	noOfPeriodChoices = [
		{ label: 'One period', value: 1 },
		{ label: '2 periods', value: 2 },
		{ label: '3 periods', value: 3 },
		{ label: '6 periods', value: 6 },
		{ label: '12 periods', value: 12 },
		{ label: '24 periods', value: 24 },
		{ label: '48 periods', value: 48 },
	];

	detailChoices = [
		{ label: 'Summary only', value: 'Summary' },
		{ label: 'Detail and summary', value: 'Detail' },
	];

	constructor(
		private location: Location,
		private generalLedgerService: GeneralLedgerService,
		private taxesService: TaxesService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private fb: UntypedFormBuilder
	) {}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		forkJoin({
			periodsRes: this.generalLedgerService.getPeriods(),
			authRes: this.taxesService.getTaxAuthorities(),
		}).subscribe({
			next: ({ periodsRes, authRes }) => {
				const pr: any = periodsRes;
				this.periods = pr.periods || [];
				const list = Array.isArray(authRes) ? authRes : [];
				this.allAuthorities = list
					.map((a: any) => ({ taxid: parseInt(String(a.taxid), 10), description: a.description || '' }))
					.filter((a) => !isNaN(a.taxid))
					.sort((a, b) => a.taxid - b.taxid);

				const fromId = this.allAuthorities.length ? this.allAuthorities[0].taxid : null;
				const toId = this.allAuthorities.length ? this.allAuthorities[this.allAuthorities.length - 1].taxid : null;
				this.toAuthorityOptions = this.filterToAuthorities(fromId);

				this.formGroup = this.fb.group({
					from_tax: [fromId, [Validators.required]],
					to_tax: [toId, [Validators.required]],
					toperiod: [pr.default_toperiod, [Validators.required]],
					no_of_periods: [1, [Validators.required]],
					detail_or_summary: ['Summary', [Validators.required]],
				});
			},
			error: () => {
				this.globalSearchService.showNotification('Could not load periods or tax authorities.', 'danger', 'bottom', 'center');
			},
		});
	}

	filterToAuthorities(fromTax: number | null): Array<{ taxid: number; description: string }> {
		if (fromTax == null) {
			return [];
		}
		return this.allAuthorities.filter((a) => a.taxid >= fromTax);
	}

	onFromTaxChange(value: number): void {
		this.toAuthorityOptions = this.filterToAuthorities(value);
		const curTo = this.formGroup.get('to_tax')?.value;
		if (curTo == null || parseInt(String(curTo), 10) < value) {
			const last = this.toAuthorityOptions[this.toAuthorityOptions.length - 1];
			this.formGroup.get('to_tax')?.setValue(last ? last.taxid : value);
		}
	}

	back(): void {
		this.location.back();
	}

	/** Short, filesystem-safe name — never use report subtitle (comma-joined authority names). */
	private exportDownloadBasename(): string {
		const day = new Date().toISOString().slice(0, 10);
		const tp = this.formGroup?.get('toperiod')?.value;
		const p = tp != null && tp !== '' ? `_p${String(tp)}` : '';
		return `TaxAuthorityReport${p}_${day}`;
	}

	runReport(): void {
		if (!this.formGroup?.valid) {
			return;
		}
		this.loading = true;
		const v = this.formGroup.value;
		const payload = {
			from_tax: parseInt(String(v.from_tax), 10),
			to_tax: parseInt(String(v.to_tax), 10),
			toperiod: parseInt(String(v.toperiod), 10),
			no_of_periods: parseInt(String(v.no_of_periods), 10),
			detail_or_summary: v.detail_or_summary as 'Detail' | 'Summary',
		};
		this.generalLedgerService.getTaxAuthorityReport(payload).subscribe({
			next: (res: TaxAuthorityReportPayload) => {
				this.loading = false;
				if (!res.success) {
					this.globalSearchService.showNotification(res.error || 'Report failed', 'danger', 'bottom', 'center');
					this.report = null;
					return;
				}
				this.report = res;
			},
			error: () => {
				this.loading = false;
				this.report = null;
			},
		});
	}

	exportXlsx(): void {
		if (!this.report?.has_data || !this.printTableRef?.nativeElement) {
			return;
		}
		this.exporting = true;
		const encoded = this.globalSearchService.base64encode(this.printTableRef.nativeElement.innerHTML);
		const base = this.exportDownloadBasename();
		const data = {
			content: encoded,
			filename: base,
		};
		this.printService.xls(data).subscribe({
			next: (result: any) => {
				this.exporting = false;
				this.globalSearchService.downloadXls(result.content, `${base}.xlsx`);
			},
			error: () => {
				this.exporting = false;
			},
		});
	}

	exportPdf(): void {
		if (!this.report?.has_data || !this.printTableRef?.nativeElement) {
			return;
		}
		this.exporting = true;
		const encoded = this.globalSearchService.base64encode(this.printTableRef.nativeElement.innerHTML);
		const base = this.exportDownloadBasename();
		const data = {
			content: encoded,
			filename: base,
			landscape: true,
		};
		this.printService.pdf(data).subscribe({
			next: (result: any) => {
				this.exporting = false;
				this.globalSearchService.downloadPdf(result.content, `${base}.pdf`);
			},
			error: () => {
				this.exporting = false;
			},
		});
	}
}
